import type { AuthUser } from '@/app/context/AuthContext';

const COOKIE_NAME = 'kadelio_last_context';

function readLastContextCookie(): 'user' | 'partner' | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|; )kadelio_last_context=(user|partner)/);
  return (match?.[1] as 'user' | 'partner') ?? null;
}

/**
 * Renvoie la route a utiliser quand un user authentifie veut acceder a son espace.
 * Centralise la logique pour HomeNav (bouton Tableau de bord), GuestRoute (post-login)
 * et toute autre navigation contextuelle.
 *
 * - ADMIN -> /admin/a-traiter (page d'accueil de l'administration)
 * - User pur partenaire (cree via Stripe) -> /espace-partenaire
 * - User hybride (compte existant + partenaire) -> cookie ou /login/choisir
 * - User pur -> /dashboard
 */
export function getPostAuthRoute(user: AuthUser | null): string {
  if (!user) return '/login';
  if (user.role === 'ADMIN') return '/admin/a-traiter';
  if (user.partnerId && user.createdViaPartnership) return '/espace-partenaire';
  if (user.partnerId) {
    const remembered = readLastContextCookie();
    if (remembered === 'partner') return '/espace-partenaire';
    if (remembered === 'user') return '/dashboard';
    return '/login/choisir';
  }
  return '/dashboard';
}

/** Page a rouvrir apres une connexion Google (le parametre ne traverse pas l'aller-retour). */
const REDIRECT_KEY = 'kadelio_redirect_apres_connexion';

/**
 * Chemin interne sur lequel revenir apres la connexion (QA-03) ou l'inscription
 * (N2-05) : un seul « / » en tete, ni barre oblique inverse, ni espace, ni
 * caractere de controle (le navigateur les retire : « /<tab>/hote » vaudrait
 * « //hote », un autre site), 512 caracteres au plus, et jamais les pages de
 * connexion ou d'inscription elles-memes.
 */
const INTERNAL_PATH = /^\/(?![/\\])[^\s\\]*$/;
const hasControlChar = (v: string): boolean => [...v].some((c) => c.charCodeAt(0) < 32 || c.charCodeAt(0) === 127);
const AUTH_PAGES = ['/login', '/register', '/verify-email', '/oauth-callback', '/forgot-password', '/reset-password'];

export function safeRedirect(raw: string | null | undefined): string | null {
  if (!raw || raw.length > 512 || !INTERNAL_PATH.test(raw) || hasControlChar(raw)) return null;
  const path = raw.split(/[?#]/)[0];
  if (AUTH_PAGES.some((p) => path === p || path.startsWith(`${p}/`)) || path.startsWith('/auth/')) return null;
  return raw;
}

/**
 * /login, avec la page demandee a rouvrir apres la connexion. `expired` :
 * la session vient d'expirer, la connexion le dit (N2-03).
 */
export function loginUrlFor(location: { pathname: string; search?: string; hash?: string }, opts: { expired?: boolean; passwordChanged?: boolean } = {}): string {
  const target = `${location.pathname}${location.search ?? ''}${location.hash ?? ''}`;
  const params = new URLSearchParams();
  if (opts.passwordChanged) params.set('raison', 'mot-de-passe');
  else if (opts.expired) params.set('raison', 'expiree');
  if (location.pathname !== '/' && safeRedirect(target)) params.set('redirect', target);
  return params.size ? `/login?${params.toString()}` : '/login';
}

const EXPIRED_KEY = 'kadelio_session_expiree';

/** Meme phrase partout ou la session s'arrete d'elle-meme (N2-03), en bandeau sur la connexion (16/10). */
export const SESSION_EXPIRED_MESSAGE = 'Par sécurité, vous avez été déconnecté. Reconnectez-vous pour continuer.';

/** Session coupee apres un changement de mot de passe sur un autre appareil (USR-15, suite S-2). */
export const PASSWORD_CHANGED_MESSAGE = 'Votre mot de passe a changé. Reconnectez-vous avec le nouveau mot de passe.';

/**
 * Session arretee d'elle-meme : a dire sur la page de connexion. `mot-de-passe` :
 * coupee par un changement de mot de passe (S-2), et cela l'emporte sur une
 * expiration notee ensuite (les appels partis apres la coupure n'ont plus de jeton).
 */
export function markSessionExpired(reason: 'expiree' | 'mot-de-passe' = 'expiree'): void {
  try {
    if (reason === 'expiree' && sessionStorage.getItem(EXPIRED_KEY) === 'mot-de-passe') return;
    sessionStorage.setItem(EXPIRED_KEY, reason === 'mot-de-passe' ? 'mot-de-passe' : '1');
  } catch { /* stockage indisponible */ }
}

/** Lit l'indication de session arretee, sans l'oublier (etat initial d'un ecran). */
export function peekSessionExpired(): boolean {
  try { return sessionStorage.getItem(EXPIRED_KEY) !== null; } catch { return false; }
}

/** La session notee comme arretee l'a ete par un changement de mot de passe (S-2). */
export function peekPasswordChanged(): boolean {
  try { return sessionStorage.getItem(EXPIRED_KEY) === 'mot-de-passe'; } catch { return false; }
}

/** Lit puis oublie l'indication de session arretee. */
export function takeSessionExpired(): boolean {
  try {
    const v = sessionStorage.getItem(EXPIRED_KEY) !== null;
    sessionStorage.removeItem(EXPIRED_KEY);
    return v;
  } catch {
    return false;
  }
}

/**
 * Ou aller une fois connecte : la page demandee si elle est dans l'espace de
 * la personne, sinon son espace (getPostAuthRoute). Un admin garde sa file,
 * sauf page de l'administration demandee ; un compte pur partenaire, son
 * espace sauf page partenaire demandee.
 */
export function postAuthTarget(user: AuthUser | null, redirect: string | null): string {
  const target = safeRedirect(redirect);
  if (!user || !target) return getPostAuthRoute(user);
  if (user.role === 'ADMIN') return target.startsWith('/admin') ? target : getPostAuthRoute(user);
  if (user.partnerId && user.createdViaPartnership) return target.startsWith('/espace-partenaire') ? target : getPostAuthRoute(user);
  return target;
}

export function rememberRedirect(redirect: string | null): void {
  try {
    const target = safeRedirect(redirect);
    if (target) sessionStorage.setItem(REDIRECT_KEY, target);
    else sessionStorage.removeItem(REDIRECT_KEY);
  } catch { /* stockage indisponible : retour a l'espace par defaut */ }
}

/** Page memorisee avant la connexion Google, lue une seule fois. */
export function takeRememberedRedirect(): string | null {
  try {
    const target = safeRedirect(sessionStorage.getItem(REDIRECT_KEY));
    sessionStorage.removeItem(REDIRECT_KEY);
    return target;
  } catch {
    return null;
  }
}

export { COOKIE_NAME as LAST_CONTEXT_COOKIE };
